import React, { useState } from 'react';
import { getAdminBadge } from '../../utils/getAdminBadge';

// Size variants: l (default, GPS), m (compact, Visits), s (dense).
const SIZES = {
  l: { pad: 'p-4', gap: 'space-y-2.5 sm:space-y-3', icon: 'w-4 h-4 sm:w-5 sm:h-5', label: 'text-xs sm:text-sm', value: 'text-2xl sm:text-3xl', meta: 'text-xs sm:text-sm' },
  m: { pad: 'p-3.5', gap: 'space-y-2', icon: 'w-4 h-4', label: 'text-xs', value: 'text-xl sm:text-2xl', meta: 'text-xs' },
  s: { pad: 'p-2.5', gap: 'space-y-1.5', icon: 'w-3.5 h-3.5', label: 'text-[11px]', value: 'text-lg', meta: 'text-[11px]' },
};

const StatTileNew = ({
  admin = false,
  value,
  label,
  icon,
  color = 'blue',
  subValue = null,
  previousValue = null,
  size = 'l',
  className = '',
  additionalInfo = null,
  onClick = null,
  variation: propVariation,
  difference: propDifference,
  description = null,
  comparisonLabel = 'sur la période précédente',
  // When true, a decrease (negative variation) is GOOD (emerald) and an increase
  // is BAD (red). Use for "lower is better" metrics like resolution/shipping time.
  invertVariationColor = false
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Extraire les valeurs numériques
  const extractNumericValue = (val) => {
    if (val === null || val === undefined) return null;

    // Si la valeur contient un %, tenter d'extraire le nombre
    if (typeof val === 'string') {
      const numVal = parseFloat(val.replace(/[^\d.-]/g, ''));
      return isNaN(numVal) ? val : numVal;
    }

    return parseFloat(val);
  };

  // Calcul de la variation en pourcentage
  const calculateVariation = () => {
    if (previousValue === null || isNaN(parseFloat(value)) || isNaN(parseFloat(previousValue))) {
      return null;
    }

    // Extraire les nombres des valeurs (au cas où ils contiennent des caractères comme %)
    const currentVal = parseFloat(value.toString().replace(/[^\d.-]/g, ''));
    const prevVal = parseFloat(previousValue.toString().replace(/[^\d.-]/g, ''));

    if (prevVal === 0) {
      return currentVal > 0 ? '+∞' : '0';
    }

    const variation = ((currentVal - prevVal) / Math.abs(prevVal)) * 100;

    // Formater avec 1 chiffre après la virgule
    const formattedVariation = variation.toFixed(1);

    return variation > 0
      ? `+${formattedVariation}%`
      : `${formattedVariation}%`;
  };

  // Calculer la différence numérique absolue
  const calculateNumericDifference = () => {
    if (previousValue === null || isNaN(parseFloat(value)) || isNaN(parseFloat(previousValue))) {
      return null;
    }

    const currentVal = extractNumericValue(value);
    const prevVal = extractNumericValue(previousValue);

    if (prevVal === null || currentVal === null) return null;

    const diff = currentVal - prevVal;
    const prefix = diff > 0 ? '+' : '';
    return `${prefix}${Math.round(diff).toLocaleString('fr-FR')}`;
  };

  const variation = propVariation !== undefined ? propVariation : calculateVariation();
  const numericDifference = propDifference !== undefined ? propDifference : calculateNumericDifference();

  // Déterminer la couleur de la variation
  const getVariationColor = () => {
    if (!variation) return '';

    const variationValue = parseFloat(variation);
    if (Number.isNaN(variationValue) || variationValue === 0) return 'text-gray-500';

    const positiveIsGood = !invertVariationColor;
    const isGood = variationValue > 0 ? positiveIsGood : !positiveIsGood;
    return isGood ? 'text-emerald-600' : 'text-red-600';
  };

  // Format the value for display
  const formatValue = (val) => {
    if (typeof val === 'string' && val.includes('%')) {
      return val;
    }
    if (typeof val === 'number' && val > 1000) {
      return val.toLocaleString('fr-FR');
    }
    if (typeof val === 'string' && !isNaN(parseInt(val)) && parseInt(val) > 1000) {
      return parseInt(val).toLocaleString('fr-FR');
    }
    return val;
  };

  const sz = SIZES[size] || SIZES.l;

  // Every tile is a self-contained rounded bordered card — a single source of truth
  // for the look, so consumers just drop tiles in a grid (no border/divider flags).
  const wrapperClass = `rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${sz.pad} transition-shadow hover:shadow-sm`;

  return (
    <div className={`w-full ${wrapperClass} ${className}`} onClick={onClick}>
      <div className={`${sz.gap} relative`}>
        {/* Tooltip icon if description exists */}
        {description && (
          <div
            className="absolute -top-0 -right-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setShowTooltip(!showTooltip);
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3 sm:h-3.5 sm:w-3.5 cursor-help text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>

            {showTooltip && (
              <div className="absolute right-0 top-full mt-1 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50 pointer-events-none text-left">
                {description}
                <div className="absolute bottom-full right-1 -mb-1 border-4 border-transparent border-b-gray-800"></div>
              </div>
            )}
          </div>
        )}

        {/* Icon + Label */}
        <div className="flex items-center gap-1.5 sm:gap-2 !mt-0">
          {icon && (
            <div className="flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true">
              {React.cloneElement(icon, {
                className: sz.icon,
                strokeWidth: "2"
              })}
            </div>
          )}
          <span className={`${sz.label} font-medium text-gray-600 dark:text-gray-400 truncate`}>
            {label} {admin && getAdminBadge()}
          </span>
        </div>

        {/* Value */}
        <p className={`${sz.value} font-bold leading-none tracking-tight text-gray-900 dark:text-gray-50 text-left`}>
          {formatValue(value)}
          {subValue && (
            <span className="text-base sm:text-lg lg:text-xl opacity-70 ml-2">
              {subValue}
            </span>
          )}
        </p>

        {/* Variation */}
        {variation && (
          <div className={`flex flex-col gap-0.5 ${sz.meta} font-medium text-left`}>
            <span className={getVariationColor()}>
              {variation}
              {numericDifference && (
                <span className="hidden sm:inline">
                  ({numericDifference})
                </span>
              )}
            </span>
            <span className={`text-gray-500 dark:text-gray-400 ${sz.meta} hidden sm:inline`}>
              {comparisonLabel}
            </span>
          </div>
        )}

        {/* Additional Info */}
        {additionalInfo && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {additionalInfo}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatTileNew;
